// kan-111 round 2 — pure helpers for the per-candidate reference question
// set (header pill + AiReferenceQuestionsModal). No React / Apollo here so
// they can be unit-tested directly.

export type AiReferenceQuestionSource = "DEFAULT" | "MANUAL";

/** One row as the API returns it (enum KEYS). */
export type AiReferenceCandidateQuestion = {
  id?: string | null;
  templateId?: string | null;
  source: AiReferenceQuestionSource | string;
  label?: string | null;
  prompt?: string | null;
  fieldKey?: string | null;
  sortOrder?: number | null;
  isRemoved?: boolean | null;
};

export type AiReferenceCandidateQuestionSet = {
  candidateId: string;
  isCustom: boolean;
  questions: AiReferenceCandidateQuestion[];
};

/** Editable row inside the modal; `key` is a stable client-only React key. */
export type QuestionDraft = {
  key: string;
  id: string | null;
  templateId: string | null;
  source: AiReferenceQuestionSource;
  label: string;
  prompt: string;
  fieldKey: string | null;
  isRemoved: boolean;
};

export const isDefaultSource = (source?: string | null) =>
  source !== "MANUAL";

/** Questions that will actually be asked (non-removed). */
export const activeQuestionCount = (
  questions?: Array<{ isRemoved?: boolean | null }> | null
): number => (questions ?? []).filter((q) => !q?.isRemoved).length;

/** Header pill text: "Default questions · 9" or "Custom · 7". */
export const questionPillLabel = (
  set?: AiReferenceCandidateQuestionSet | null
): string => {
  const n = activeQuestionCount(set?.questions);
  return set?.isCustom ? `Custom · ${n}` : `Default questions · ${n}`;
};

let draftSeq = 0;
const nextKey = () => `q${++draftSeq}`;

/** API rows → modal drafts, ordered by sortOrder (stable on ties). */
export const toDrafts = (
  questions?: AiReferenceCandidateQuestion[] | null
): QuestionDraft[] =>
  (questions ?? [])
    .map((q, i) => ({ q, i }))
    .sort(
      (a, b) =>
        (a.q.sortOrder ?? a.i) - (b.q.sortOrder ?? b.i) || a.i - b.i
    )
    .map(({ q }) => ({
      key: q.id || q.templateId || nextKey(),
      id: q.id ?? null,
      templateId: q.templateId ?? null,
      source: isDefaultSource(q.source) ? "DEFAULT" : "MANUAL",
      label: q.label ?? "",
      prompt: q.prompt ?? "",
      fieldKey: q.fieldKey ?? null,
      isRemoved: !!q.isRemoved,
    }));

export const newManualDraft = (): QuestionDraft => ({
  key: nextKey(),
  id: null,
  templateId: null,
  source: "MANUAL",
  label: "",
  prompt: "",
  fieldKey: null,
  isRemoved: false,
});

/** First client-side problem with the draft, or null when it can be saved. */
export const validateDrafts = (drafts: QuestionDraft[]): string | null => {
  const manual = drafts.filter((d) => d.source === "MANUAL");
  if (manual.some((d) => !d.label.trim()))
    return "Every custom question needs a label.";
  if (manual.some((d) => !d.prompt.trim()))
    return "Every custom question needs the question text.";
  if (!activeQuestionCount(drafts))
    return "Keep at least one question for the reference call.";
  return null;
};

/**
 * Drafts → mutation input rows, in display order with sortOrder = index.
 * DEFAULT rows only carry identity + isRemoved (wording is locked, D3);
 * MANUAL rows carry their trimmed label / prompt.
 */
export const toSaveQuestions = (drafts: QuestionDraft[]) =>
  drafts.map((d, index) =>
    d.source === "DEFAULT"
      ? {
          id: d.id,
          templateId: d.templateId,
          source: "DEFAULT",
          isRemoved: d.isRemoved,
          sortOrder: index,
        }
      : {
          id: d.id,
          templateId: null,
          source: "MANUAL",
          label: d.label.trim(),
          prompt: d.prompt.trim(),
          isRemoved: false,
          sortOrder: index,
        }
  );
